import React from 'react'
import { ResponsiveContainer } from 'recharts'
import { Card, H4 } from '@blueprintjs/core'
import { Column } from '../ReportPage'
import { getColorScale } from '../config'
import { IGroup } from '../types'
import Donut, { IDonutData } from '@ui/charts/Donut'
import { Flex } from '@ui'
import { sumBy } from 'lodash'

interface IProps {
  chart: IGroup
  width?: number
  height?: number
  onClick?: (a: any) => void
  conf: any
  showStats?: boolean
}

export default function DonutChart({ chart, height = 200, conf, onClick }: IProps) {
  const data = formatter({
    columns: chart.columns,
    colors: getColorScale(chart.name, false),
    columnTypes: chart.columnTypes,
    conf,
    values: chart.values,
  })

  return (
    <Column>
      <Card style={{ height: '100%', width: '100%' }}>
        <H4>{chart.name}</H4>
        <Flex justify="flex-end" direction="column">
          <ResponsiveContainer height={height} width="100%">
            <Donut data={data} title={chart.name} unit={conf.unit} onClick={onClick} />
          </ResponsiveContainer>
        </Flex>
      </Card>
    </Column>
  )
}

const formatValue = (columnType: string, value: string) =>
  ['int', 'decimal', 'percent'].indexOf(columnType) >= 0 ? +value : value

const formatter = ({
  columns,
  columnTypes,
  values,
  conf,
  colors,
}: {
  columns: string[] | string
  columnTypes: string[] | string
  values: any
  conf: any
  colors: any
}): IDonutData[] => {
  const data = values.map((entry: any, idx: any) => {
    const d = entry.reduce((obj: any, value: any, i: any) => {
      switch (columns[i]) {
        case 'id':
        case 'Id':
          return { ...obj, id: value }

        case conf.value:
          return {
            ...obj,
            value: formatValue(columnTypes[i], value),
          }

        case conf.label:
          return {
            ...obj,
            label: formatValue(columnTypes[i], value),
          }

        default:
          return obj
      }
    }, {})

    const id = d.id || d.label
    return { ...d, id }
  })

  data.sort((d1: any, d2: any) => d2.value - d1.value)

  return (
    data.length > 6
      ? // Show only top 4 entries and group the rest into `Other` category
        [
          ...data.slice(0, 4),
          { label: 'Other', value: sumBy(data.slice(4), 'value'), color: 'eee' },
        ]
      : data
  ).map((item: { id: string; label: string; value: number }) => ({
    ...item,
    color: `#${colors(item.id)}`,
  }))
}
